import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Button,
  Card,
  Select,
  TextInput,
  ToastProvider,
  useToast
} from '../../design-system';
import { voiceApi } from './voiceClient';

const VOICE_OPTIONS = [
  { value: 'eve', label: 'Eve — warm, friendly (default)' },
  { value: 'leo', label: 'Leo — calm, conversational' },
  { value: 'rex', label: 'Rex — energetic, direct' },
  { value: 'aurora', label: 'Aurora — bright, upbeat' },
  { value: 'classic', label: 'Classic — neutral' }
];

const LANGUAGE_OPTIONS = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es-MX', label: 'Spanish (Mexico)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
  { value: 'auto', label: 'Auto-detect (Grok cloud only)' }
];

const PROVIDER_OPTIONS = [
  { value: 'auto', label: 'Auto (cloud first, edge fallback)' },
  { value: 'cloud', label: 'Force xAI Grok cloud' },
  { value: 'edge', label: 'Force Jetson edge (offline)' }
];

interface Props {
  storeId: string;
}

const Inner: React.FC<Props> = ({ storeId }) => {
  const { getIdToken } = useAuth();
  const { show } = useToast();
  const [persona, setPersona] = React.useState('eve');
  const [language, setLanguage] = React.useState('en-US');
  const [provider, setProvider] = React.useState('auto');
  const [vad, setVad] = React.useState(true);
  const [upsell, setUpsell] = React.useState(true);
  const [pinThreshold, setPinThreshold] = React.useState('5000');
  const [saving, setSaving] = React.useState(false);
  const [contextInfo, setContextInfo] = React.useState<{ bytes: number; itemCount: number; compiledAt: string } | null>(null);
  const [rebuilding, setRebuilding] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const cfg: any = await voiceApi.getConfig(getIdToken, storeId);
        if (cfg.voicePersona) setPersona(cfg.voicePersona);
        if (cfg.language) setLanguage(cfg.language);
        if (cfg.preferredProvider) setProvider(cfg.preferredProvider);
        if (typeof cfg.vadEnabled === 'boolean') setVad(cfg.vadEnabled);
        if (typeof cfg.upsellEnabled === 'boolean') setUpsell(cfg.upsellEnabled);
        if (typeof cfg.managerPinThresholdCents === 'number') setPinThreshold(String(cfg.managerPinThresholdCents));
      } catch { /* no config yet — defaults apply */ }
    })();
  }, [getIdToken, storeId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await voiceApi.updateConfig(getIdToken, storeId, {
        storeId,
        voicePersona: persona as any,
        language,
        preferredProvider: provider as any,
        vadEnabled: vad,
        upsellEnabled: upsell,
        managerPinThresholdCents: parseInt(pinThreshold, 10) || 0
      });
      show({ tone: 'success', title: 'Voice config saved' });
    } catch (err: any) {
      show({ tone: 'error', title: 'Save failed', description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const refreshContext = React.useCallback(async () => {
    try {
      const c = await voiceApi.getContext(getIdToken, storeId);
      setContextInfo({ bytes: c.bytes, itemCount: c.itemCount, compiledAt: c.compiledAt });
    } catch (err: any) {
      if (err.code === 'error') return;
      setContextInfo(null);
    }
  }, [getIdToken, storeId]);

  React.useEffect(() => { refreshContext(); }, [refreshContext]);

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      const r = await voiceApi.rebuildContext(getIdToken, storeId);
      setContextInfo({ bytes: r.bytes, itemCount: r.itemCount, compiledAt: r.compiledAt });
      show({ tone: 'success', title: 'Menu context rebuilt', description: `${r.itemCount} items, ${(r.bytes / 1024).toFixed(1)} KB` });
    } catch (err: any) {
      show({ tone: 'error', title: 'Rebuild failed', description: err.message });
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <div data-theme="dark" style={{ padding: 'var(--bso-space-8) var(--bso-space-10)', background: 'var(--bso-bg-base)', color: 'var(--bso-text-primary)', fontFamily: 'var(--bso-font-sans)' }}>
      <h1 style={{ margin: '0 0 var(--bso-space-2)' }}>Voice configuration</h1>
      <p style={{ color: 'var(--bso-text-secondary)', maxWidth: 640, marginBottom: 'var(--bso-space-8)' }}>
        Per-store voice agent settings. Persona and language apply when the call
        runs through xAI Grok (cloud). Edge fallback always uses the locally
        loaded Whisper + Llama + Piper voices.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--bso-space-6)' }}>
        <Card>
          <Card.Header title="Voice persona & language" />
          <Card.Body>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bso-space-4)' }}>
              <Select label="Persona" value={persona} onChange={(e) => setPersona(e.target.value)} options={VOICE_OPTIONS} />
              <Select label="Language" value={language} onChange={(e) => setLanguage(e.target.value)} options={LANGUAGE_OPTIONS} />
              <Select label="Provider preference" value={provider} onChange={(e) => setProvider(e.target.value)} options={PROVIDER_OPTIONS} />
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title="Behavior" />
          <Card.Body>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bso-space-4)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--bso-space-3)' }}>
                <input type="checkbox" checked={vad} onChange={(e) => setVad(e.target.checked)} />
                <span>Voice activity detection (auto cut on silence)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--bso-space-3)' }}>
                <input type="checkbox" checked={upsell} onChange={(e) => setUpsell(e.target.checked)} />
                <span>Suggest upsells based on order context</span>
              </label>
              <TextInput
                label="Manager PIN required above"
                hint="Charge in cents above which the agent prompts for a manager PIN"
                value={pinThreshold}
                onChange={(e) => setPinThreshold(e.target.value.replace(/\D/g, ''))}
                prefix="¢"
              />
            </div>
          </Card.Body>
          <Card.Footer>
            <Button variant="ghost" disabled={saving}>Reset</Button>
            <Button onClick={handleSave} loading={saving}>Save settings</Button>
          </Card.Footer>
        </Card>

        <Card>
          <Card.Header
            title="Menu context"
            actions={<Button size="sm" onClick={handleRebuild} loading={rebuilding}>Rebuild now</Button>}
          />
          <Card.Body>
            {contextInfo ? (
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8, color: 'var(--bso-text-secondary)' }}>
                <li>Items: <strong style={{ color: 'var(--bso-text-primary)' }}>{contextInfo.itemCount}</strong></li>
                <li>Size: <strong style={{ color: 'var(--bso-text-primary)' }}>{(contextInfo.bytes / 1024).toFixed(1)} KB</strong> / 50 KB cap</li>
                <li>Last compiled: <strong style={{ color: 'var(--bso-text-primary)' }}>{new Date(contextInfo.compiledAt).toLocaleString()}</strong></li>
              </ul>
            ) : (
              <p style={{ margin: 0, color: 'var(--bso-text-secondary)' }}>
                No context compiled yet. Click <em>Rebuild now</em> to generate one from the current menu.
              </p>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title="Provider tier order" />
          <Card.Body>
            <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
              <li>xAI Grok (cloud) — primary</li>
              <li>Jetson edge (Whisper + Llama + Piper) — offline fallback</li>
              <li>OpenAI GPT-4 Lambda — emergency only</li>
              <li>Android on-device STT — degraded (no reasoning, no TTS)</li>
            </ol>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export const VoiceConfigurationPanel: React.FC<Props> = (props) => (
  <ToastProvider>
    <Inner {...props} />
  </ToastProvider>
);
